import { useContext, useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams, matchPath } from "react-router-dom";
import { toast } from "react-toastify";

import ActionButton from "../formInputs/buttons/ActionButton";
import DcsdDialog from "../modals/DcsdDialog";
import ElementaryProgressReportDao from "../../dao/ElementaryProgressReportDao";
import HomeroomGroups from "./HomeroomGroups";
import isGradingProfileWindowOpen from "../../utils/ActionControls/isGradingProfileWindowOpen";
import isEprEditable from "../../utils/ActionControls/isEprEditable";
import LoadingSvg from "../loadingSvg/LoadingSvg";
import StudentRosterTable from "./StudentRosterTable";
import TeacherInfoDao from "../../dao/TeacherInfoDao";

import { GlobalContext } from "../contextProvider/ContextProvider";

import "../../styles/ManageRoster.scss";
import "../../styles/TeacherHome.scss";

/**
 * ManageRoster component allows users to view and manage students in an EPR (Elementary Progress Report) roster.
 * Users can select an IC section, import students, and update their grading profiles.
 *
 * @component
 * @param {Object} props
 * @param {Function} dispatch
 * @param {Object} state
 * @returns {JSX.Element}
 */
const ManageRoster = ({ dispatch, path, state, basePath, ownerUsername, schoolLevelTemplateDto }) => {
    const navigate = useNavigate();
    const { gradingProfileId: paramId } = useParams();
    const { gradingProfiles, selectedEpr } = state;

    const gradingProfileId = useMemo(() => {
        if (paramId) return paramId;
        const match = matchPath(`${basePath || ""}/summary/roster/:id/*`, path);
        const matchedId = match?.params?.id;
        return matchedId && !Number.isNaN(Number(matchedId)) ? matchedId : null;
    }, [paramId, path, basePath]);

    const { state: globalState } = useContext(GlobalContext);
    const { actionWindowsDto, currentTimePeriodDto, userDetails, schoolYearDto, token } = globalState || {};

    const [currentRosteredSection, setCurrentRosteredSection] = useState(null);
    const [deltaStudents, setDeltaStudents] = useState([]);
    const [isActiveWindow, setIsActiveWindow] = useState(false);
    const [loader, setLoader] = useState(false);
    const [open, setOpen] = useState("false");
    const [showSectionSelect, setShowSectionSelect] = useState(true);
    const [sectionListDto, setSectionListDto] = useState(null);
    const [selectedRoster, setSelectedRoster] = useState("");
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [collisionStudents, setCollisionStudents] = useState(null);
    const [prevOwnerUsername, setPrevOwnerUsername] = useState(ownerUsername);

    const [viewMode, setViewMode] = useState("WHOLE_CLASS");

    if (ownerUsername !== prevOwnerUsername) {
        setPrevOwnerUsername(ownerUsername);
        setSectionListDto(null);
    }

    const gradingProfilesCurrentSem = useMemo(() => {
        return (gradingProfiles || []).filter((p) => Number(p.timePeriod) === Number(currentTimePeriodDto?.key));
    }, [gradingProfiles, currentTimePeriodDto?.key]);

    /**
     * Extracts section IDs already assigned to students in the selected EPR.
     */
    const usedSections = useMemo(() => {
        return selectedEpr?.gradingProfileStudents?.map((student) => Number(student.sectionId)) || [];
    }, [selectedEpr]);

    /**
     * Filters out sections already assigned to the selected EPR.
     */
    const filteredSections = useMemo(() => {
        return sectionListDto?.filter((section) => !usedSections.includes(section.id)) || [];
    }, [sectionListDto, usedSections]);

    /**
     * Determines if multiple grading profiles exist, requiring a selection toggle.
     */
    const shouldShowEprSelect = useMemo(() => {
        return !gradingProfileId && gradingProfilesCurrentSem?.length > 1;
    }, [gradingProfileId, gradingProfilesCurrentSem]);

    const sortedDeltaStudents = useMemo(() => {
        return deltaStudents.toSorted((a, b) => a.lastName.localeCompare(b.lastName));
    }, [deltaStudents]);

    /**
     * Sets the selected EPR profile.
     * @param {Object} profile
     */
    const handleEprChange = (profile) => {
        dispatch({ type: "SET_SELECTED_EPR", payload: profile });
        setDeltaStudents([]);
        setShowSectionSelect(true);
    };

    /**
     * Handles changes to the selected IC roster.
     * @param {Object} e
     */
    const handleRosterChange = (e) => {
        setSelectedRoster(e.target.value);
    };

    /**
     * Handles importing an IC section into the grading profile.
     * If a roster already exists, prompts for confirmation before replacing it.
     */
    const handleImportICSection = () => {
        if (selectedEpr?.gradingProfileStudents?.length > 0) {
            setOpen("confirm-replace-roster");
        } else {
            importNewSection();
        }
    };

    const handleClearAndImport = () => {
        const clearOptions = {
            action: "clearStudentsFromProfile",
            gradingProfileId: selectedEpr.key,
            token
        };

        ElementaryProgressReportDao(clearOptions)
            .then(() => {
                setDeltaStudents([]);
                setCollisionStudents([]);
                setOpen("false");
                importNewSection();
            })
            .catch((error) => {
                toast.error("There was a problem clearing Report Card roster. Please try again later.");
                console.error(error);
            });
    };

    /**
     * Imports a new section by fetching students from the IC system.
     */
    const importNewSection = () => {
        const secId = selectedRoster;
        if (!secId) return;

        const options = {
            action: "rosterBySectionRead",
            sectionId: secId,
            token
        };

        TeacherInfoDao(options)
            .then((response) => {
                if (!response) {
                    throw new Error("Failed to roster selected section");
                }
                const { payload } = response.data;
                const arrayOfStudents = (payload?.sectionRoster || []).map((student) => ({
                    key: null,
                    gradingProfile: selectedEpr.key,
                    studentNumber: student.studentNumber,
                    sectionId: secId
                }));

                const addStudentsOptions = {
                    action: "addStudentsToProfile",
                    gradingProfileId: selectedEpr.key,
                    data: arrayOfStudents,
                    token
                };

                ElementaryProgressReportDao(addStudentsOptions)
                    .then((response) => {
                        const payload = response?.data?.payload;
                        if (!payload) {
                            toast.error(
                                "There was a problem adding selected students to Report Card roster. Please try again later."
                            );
                            return;
                        }

                        const { gradingProfileDto: updatedProfile, studentsNotAddedInRoster = [] } = payload;

                        dispatch({ type: "SET_SELECTED_EPR", payload: updatedProfile });
                        dispatch({
                            type: "SET_PROFILES",
                            payload: state.gradingProfiles.map((profile) =>
                                profile.key === updatedProfile.key ? updatedProfile : profile
                            )
                        });

                        if (studentsNotAddedInRoster.length) {
                            setCollisionStudents(studentsNotAddedInRoster);
                            setOpen("student-not-added");
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                        toast.error("There was a problem rostering selected section. Please try again later.");
                    });
            })
            .catch((error) => {
                console.error(error);
                toast.error("There was a problem rostering selected section. Please try again later.");
            });
    };

    const handleAddStudents = () => {
        const sectionId = currentRosteredSection?.id ?? selectedEpr?.gradingProfileStudents?.[0]?.sectionId;
        if (!sectionId) {
            toast.error("Unable to determine the rostered IC section. Please try again later.");
            return;
        }
        if (selectedStudents.length === 0) return;

        const arrayOfStudents = selectedStudents.map((student) => ({
            key: null,
            gradingProfile: Number(selectedEpr.key),
            studentNumber: student,
            sectionId
        }));

        const options = {
            action: "addStudentsToProfile",
            gradingProfileId: selectedEpr.key,
            data: arrayOfStudents,
            token
        };
        ElementaryProgressReportDao(options)
            .then((response) => {
                if (response?.data?.payload) {
                    const updatedProfile = response.data.payload.gradingProfileDto;
                    const studentsNotAddedInRoster = response.data.payload.studentsNotAddedInRoster;

                    dispatch({ type: "SET_SELECTED_EPR", payload: updatedProfile });
                    dispatch({
                        type: "SET_PROFILES",
                        payload: state.gradingProfiles.map((profile) =>
                            profile.key === updatedProfile.key ? updatedProfile : profile
                        )
                    });
                    if (studentsNotAddedInRoster && studentsNotAddedInRoster.length > 0) {
                        setCollisionStudents(studentsNotAddedInRoster);
                        setOpen("student-not-added");
                    }
                    setSelectedStudents([]);
                } else {
                    toast.error(
                        "There was a problem adding selected students to Report Card roster. Please try again later."
                    );
                }
            })
            .catch((error) => {
                toast.error(
                    "There was a problem adding selected students to Report Card roster. Please try again later."
                );
                console.error(error);
            });
    };

    /**
     * Finds the homeroom section that matches the selected grading profile.
     * @param {Array} sections
     * @param {Object} gradingProfileInfo
     * @returns {Object|null}
     */
    const findMatchingHomeroom = (sections, gradingProfileInfo) => {
        if (!gradingProfileInfo?.gradeName) return null;

        let gradeNumber;

        if (gradingProfileInfo.gradeName.toUpperCase() === "K") {
            gradeNumber = "00";
        } else {
            gradeNumber = gradingProfileInfo.gradeName.replace(/\D/g, "").padStart(2, "0");
        }

        return sections.find(
            (section) =>
                section.sectionCourseName.includes("Homeroom") && section.sectionCourseNumber.slice(-2) === gradeNumber
        );
    };

    const handleSelectAll = () => {
        if (selectedStudents.length === deltaStudents.length) {
            setSelectedStudents([]);
        } else {
            setSelectedStudents(sortedDeltaStudents.map((student) => student.studentNumber));
        }
    };

    const handleCheckboxChange = (id) => {
        setSelectedStudents((prevSelected) =>
            prevSelected.includes(id) ? prevSelected.filter((studentId) => studentId !== id) : [...prevSelected, id]
        );
    };

    const handleViewSwitch = (type) => {
        setViewMode(type);
        const prefix = basePath || "/home";
        let url = gradingProfileId ? `${prefix}/summary/roster/${gradingProfileId}` : `${prefix}/summary/roster`;
        if (type === "GROUP") {
            url = `${url}/groups`;
        }
        navigate(url);
    };

    /**
     * Selects the appropriate homeroom section based on the selected EPR.
     */
    useEffect(() => {
        if (!selectedRoster && selectedEpr && sectionListDto?.length > 0) {
            const matchedHomeroom = findMatchingHomeroom(filteredSections, selectedEpr);
            const selectedIcRoster = selectedEpr.gradingProfileStudents.length > 0 ? "" : matchedHomeroom?.id;
            setSelectedRoster(selectedIcRoster || "");
        }
    }, [filteredSections, sectionListDto?.length, selectedEpr, selectedRoster]);

    /**
     * Selects the appropriate homeroom section based on the selected EPR.
     */
    useEffect(() => {
        if (path) {
            const view = path.includes("groups") ? "GROUP" : "WHOLE_CLASS";
            setViewMode(view);
        }
    }, [path]);

    /**
     * Loads available IC sections when the component mounts.
     */
    useEffect(() => {
        if (!sectionListDto && token) {
            setLoader(true);
            const options = {
                action: "teacherSectionsRead",
                username: ownerUsername || userDetails.username,
                token
            };
            TeacherInfoDao(options)
                .then((response) => {
                    if (response) {
                        const { payload } = response.data;
                        // teacher-info-sb returns section id as `key` (legacy used `id`).
                        const sections = (Array.isArray(payload) ? payload : []).map((sec) => ({
                            ...sec,
                            id: Number(sec.key ?? sec.id)
                        }));

                        const primarySections = sections.filter((sec) =>
                            (sec.teachers || []).some(
                                (t) =>
                                    (t.username === userDetails.username || t.username === ownerUsername) &&
                                    t.staffDesignator === "PRIMARY_TEACHER"
                            )
                        );

                        const uniqueById = primarySections.filter(
                            (obj, index, arr) => index === arr.findIndex((o) => o.id === obj.id)
                        );
                        setSectionListDto(uniqueById);
                    } else {
                        toast.error("There was a problem retrieving available IC sections. Please try again later.");
                    }
                })
                .catch((error) => {
                    toast.error("There was a problem retrieving available IC sections. Please try again later.");
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [sectionListDto, token, userDetails, ownerUsername]);

    /**
     * Sets the default selected EPR profile when the component mounts.
     */
    useEffect(() => {
        if (!selectedEpr && gradingProfilesCurrentSem.length > 0) {
            let profileToSet = gradingProfilesCurrentSem[0];

            if (gradingProfileId) {
                profileToSet = gradingProfilesCurrentSem.find(
                    (profile) => Number.parseInt(profile.key, 10) === Number.parseInt(gradingProfileId, 10)
                );
            }

            dispatch({ type: "SET_SELECTED_EPR", payload: profileToSet });
        }
    }, [selectedEpr, gradingProfilesCurrentSem, gradingProfileId, dispatch]);

    /**
     * Finds missing students that exist in IC but not in EPR roster
     */
    useEffect(() => {
        if (selectedEpr) {
            const options = {
                action: "getStudentRosterDelta",
                gradingProfileId: selectedEpr.key,
                token
            };
            ElementaryProgressReportDao(options)
                .then((response) => {
                    if (response?.data?.payload) {
                        setDeltaStudents(response.data.payload.sectionRoster);
                    }
                    if (response?.data?.errors?.length > 0) {
                        toast.error("There was a problem loading possible students missing from IC section.");
                    }
                })
                .catch((error) => {
                    toast.error("There was a problem loading possible students missing from IC section.");
                    console.error(error);
                });
        }
    }, [selectedEpr, token]);

    /**
     * Sets current rostered section name in state and hides select
     */
    useEffect(() => {
        if (selectedEpr?.gradingProfileStudents && selectedEpr.gradingProfileStudents.length > 0 && sectionListDto) {
            const rosterSectionId = selectedEpr.gradingProfileStudents[0].sectionId;
            const currentSection = sectionListDto.find((sec) => String(sec.id) === String(rosterSectionId));
            setCurrentRosteredSection(currentSection ?? null);
            setShowSectionSelect(false);
        }
    }, [sectionListDto, selectedEpr]);

    useEffect(() => {
        if (actionWindowsDto && selectedEpr) {
            const profileTimePeriod = selectedEpr.timePeriod;
            const isWindowOpen = isGradingProfileWindowOpen(actionWindowsDto, profileTimePeriod);
            setIsActiveWindow(isWindowOpen);
        }
    }, [actionWindowsDto, selectedEpr]);

    const profileStatus =
        gradingProfilesCurrentSem.find((p) => String(p.key) === String(selectedEpr?.key))?.status ??
        selectedEpr?.status;
    const isActiveAndIsUnreleased = isEprEditable({
        isActiveWindow,
        status: profileStatus,
        userDetails
    });
    const rosterSectionId = currentRosteredSection?.id ?? selectedEpr?.gradingProfileStudents?.[0]?.sectionId;
    const canAddDeltaStudents = isActiveAndIsUnreleased && Boolean(rosterSectionId) && selectedStudents.length > 0;
    const noGroupShares =
        selectedEpr?.gradingProfileStudents?.length > 0 &&
        (selectedEpr?.homeroomGroups?.every(
            (g) =>
                (!g.homeroomGroupShares || g.homeroomGroupShares.length === 0) &&
                (!g.homeroomGroupSleShares || g.homeroomGroupSleShares.length === 0)
        ) ??
            true);

    return (
        <div className="right-side-wrapper">
            {noGroupShares && (
                <>
                    <div className="no-shares-warning">
                        You are not currently sharing your Report Card. Please{" "}
                        <button onClick={() => navigate(`${basePath || "/home"}/summary/share/${selectedEpr.key}`)}>
                            click here
                        </button>{" "}
                        to share with your colleagues (e.g. specials team).
                    </div>
                    <hr />
                </>
            )}
            <div className="right-page-title">
                <h4>
                    Report Card Roster{" "}
                    {selectedEpr && (
                        <span>
                            &ndash; {selectedEpr.gradeName === "K" ? "Kindergarten" : `${selectedEpr.gradeName} Grade`}
                        </span>
                    )}
                </h4>
                <h5>
                    <span style={{ fontWeight: "700" }}>{currentTimePeriodDto?.name}</span> | {schoolYearDto.name}
                </h5>
            </div>
            <hr />

            {!gradingProfilesCurrentSem ||
                (gradingProfilesCurrentSem.length === 0 && (
                    <div>
                        Roster management is unavailable until a Homeroom Report Card has been created. If your school
                        has released the grade you teach, please first create a Homeroom Report Card. Otherwise, please
                        wait until it becomes available.
                    </div>
                ))}
            {shouldShowEprSelect && (
                <section className="epr-selector-roster">
                    <h5>Select a Grade:</h5>
                    <label className="visually-hidden" htmlFor="eprSelectManageRoster">
                        Select a Grade
                    </label>
                    <select
                        className="epr-select-dropdown"
                        id="eprSelectManageRoster"
                        value={selectedEpr?.key || ""}
                        onChange={(e) => {
                            const selected = gradingProfiles.find(
                                (p) => p.key.toString() === e.target.value.toString()
                            );
                            handleEprChange(selected);
                            handleViewSwitch("WHOLE_CLASS");
                        }}
                    >
                        <option value="" disabled>
                            Select Grade
                        </option>
                        {gradingProfilesCurrentSem
                            .sort((a, b) => a.grade - b.grade)
                            .map((profile) => (
                                <option key={profile.key} value={profile.key}>
                                    {profile.gradeName === "K" ? "Kindergarten" : `${profile.gradeName} Grade`}
                                </option>
                            ))}
                    </select>
                </section>
            )}

            {gradingProfilesCurrentSem && gradingProfilesCurrentSem.length > 0 && (
                <>
                    {selectedEpr?.gradingProfileStudents?.length > 0 && (
                        <section className="share-mode-roster">
                            <h5>Select Roster View:</h5>
                            <div className="share-mode-options-roster">
                                <div className="mode-wrapper">
                                    <button
                                        className={`mode-button ${viewMode === "WHOLE_CLASS" ? "active" : ""}`}
                                        onClick={() => handleViewSwitch("WHOLE_CLASS")}
                                    >
                                        Homeroom Roster
                                    </button>
                                </div>
                                <div className="mode-wrapper">
                                    <button
                                        className={`mode-button ${viewMode === "GROUP" ? "active" : ""}`}
                                        onClick={() => handleViewSwitch("GROUP")}
                                    >
                                        Homeroom Groups (
                                        {
                                            selectedEpr?.homeroomGroups?.filter((g) => !g.name.includes("INTERNAL_"))
                                                .length
                                        }
                                        )
                                    </button>
                                </div>
                            </div>
                        </section>
                    )}
                    {selectedEpr?.gradingProfileStudents?.length > 0 && <hr />}
                    {viewMode === "WHOLE_CLASS" && (
                        <>
                            {selectedEpr?.gradingProfileStudents && selectedEpr?.gradingProfileStudents.length > 0 && (
                                <div
                                    className={`current-roster ${showSectionSelect || deltaStudents.length > 0 ? "no-margin" : "bottom-margin"}`}
                                >
                                    <h5>
                                        Current Rostered Section:{" "}
                                        <span>
                                            {currentRosteredSection?.sectionCourseName ??
                                                (rosterSectionId ? `Section ${rosterSectionId}` : "Unknown")}
                                        </span>
                                    </h5>
                                    <ActionButton
                                        className="action-button-reg"
                                        disable={!isActiveAndIsUnreleased}
                                        label="Change Section"
                                        onClick={() => setShowSectionSelect(true)}
                                    />
                                </div>
                            )}
                            {selectedEpr && showSectionSelect && (
                                <div className="roster-select-section">
                                    <label className="select-label-ic" htmlFor="rosterSelect">
                                        <h6 style={{ marginBottom: "0px" }}>Select an IC Section for Report Card:</h6>
                                        <select
                                            className="ic-select"
                                            disabled={!isActiveAndIsUnreleased}
                                            id="rosterSelect"
                                            name="roster-select"
                                            value={selectedRoster}
                                            onChange={handleRosterChange}
                                        >
                                            <option value="">– Select an IC Section –</option>
                                            {filteredSections?.map((section, index) => (
                                                <option key={`section-${section.id}-${index}`} value={section.id}>
                                                    {section.sectionCourseName}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <div className="no-margin-btn-roster">
                                        <ActionButton
                                            label="Import IC Section"
                                            disable={!selectedRoster || !isActiveAndIsUnreleased}
                                            onClick={handleImportICSection}
                                        />
                                    </div>
                                </div>
                            )}
                            {deltaStudents.length > 0 && (
                                <section className="delta-section">
                                    <div className="container mt-3">
                                        <div className="accordion" id="studentAccordion">
                                            <div className="accordion-item delta-border">
                                                <div className="accordion-header" id="headingOne">
                                                    <button
                                                        className="accordion-button"
                                                        type="button"
                                                        data-bs-toggle="collapse"
                                                        data-bs-target="#collapseOne"
                                                        aria-expanded="true"
                                                        aria-controls="collapseOne"
                                                    >
                                                        Students Potentially Missing from Roster
                                                    </button>
                                                </div>
                                                <div
                                                    id="collapseOne"
                                                    className="accordion-collapse collapse"
                                                    aria-labelledby="headingOne"
                                                    data-bs-parent="#studentAccordion"
                                                >
                                                    <div className="accordion-body">
                                                        <section className="delta-section-container">
                                                            <div className="delta-text">
                                                                The following students are in the original IC Section
                                                                but are not in your homeroom roster. Would you like to
                                                                add them?
                                                            </div>
                                                            <div className="delta-student-grid">
                                                                {sortedDeltaStudents.map((student) => {
                                                                    const { firstName, lastName, studentNumber } =
                                                                        student;
                                                                    const displayStudent = `${lastName}, ${firstName} (${studentNumber})`;
                                                                    return (
                                                                        <label
                                                                            key={student.id}
                                                                            className="delta-student-card"
                                                                        >
                                                                            <input
                                                                                checked={selectedStudents.includes(
                                                                                    student.studentNumber
                                                                                )}
                                                                                disabled={!isActiveAndIsUnreleased}
                                                                                name="student"
                                                                                onChange={() =>
                                                                                    handleCheckboxChange(
                                                                                        student.studentNumber
                                                                                    )
                                                                                }
                                                                                value={student.studentNumber}
                                                                                type="checkbox"
                                                                            />
                                                                            <span>{displayStudent}</span>
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                            <div className="delta-button-container">
                                                                <label className="select-all-check-btn">
                                                                    <input
                                                                        type="checkbox"
                                                                        disabled={!isActiveAndIsUnreleased}
                                                                        checked={
                                                                            selectedStudents.length ===
                                                                            deltaStudents.length
                                                                        }
                                                                        onChange={handleSelectAll}
                                                                    />
                                                                    <span>
                                                                        {selectedStudents.length ===
                                                                        deltaStudents.length
                                                                            ? "Unselect All"
                                                                            : "Select All"}
                                                                    </span>
                                                                </label>

                                                                <ActionButton
                                                                    className="action-button-reg"
                                                                    disable={!canAddDeltaStudents}
                                                                    onClick={() => handleAddStudents()}
                                                                    label="Add to Roster"
                                                                />
                                                            </div>
                                                        </section>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>
                            )}
                            <section className="epr-roster-manage-section">
                                {selectedEpr?.gradingProfileStudents?.length > 0 && (
                                    <section>
                                        <StudentRosterTable
                                            dispatch={dispatch}
                                            gradingProfileStudents={selectedEpr.gradingProfileStudents}
                                            isActiveAndIsUnreleased={isActiveAndIsUnreleased}
                                            selectedEpr={selectedEpr}
                                            setDeltaStudents={setDeltaStudents}
                                            setShowSectionSelect={setShowSectionSelect}
                                            setStudentsNotAdded={setCollisionStudents}
                                            state={state}
                                            basePath={basePath}
                                        />
                                    </section>
                                )}
                            </section>
                        </>
                    )}

                    {viewMode === "GROUP" && (
                        <HomeroomGroups
                            dispatch={dispatch}
                            state={state}
                            basePath={basePath}
                            schoolLevelTemplateDto={schoolLevelTemplateDto}
                        />
                    )}
                </>
            )}
            <DcsdDialog
                actions={
                    <div className="modal-btn-group">
                        <ActionButton
                            className="action-button-cancel"
                            label="Cancel"
                            onClick={() => setOpen("false")}
                        />
                        <ActionButton
                            className="action-button-reg"
                            label="Confirm"
                            onClick={() => {
                                handleClearAndImport();
                            }}
                        />
                    </div>
                }
                ariaLabel="Confirmation Replace Roster Dialog"
                hasCloseX={false}
                id="confirm-replace-roster"
                open={open}
                title="Clear and Replace Current Report Card Roster"
            >
                <div>
                    Please confirm that you wish to clear your current Report Card roster and set your Report Card
                    roster based on the selected IC section.
                    <br />
                    <span style={{ fontStyle: "italic", textDecoration: "underline" }}>
                        Please note, you will lose any completed work with this action.
                    </span>
                </div>
            </DcsdDialog>
            <DcsdDialog
                actions={
                    <div className="modal-btn-group">
                        <ActionButton
                            className="action-button-reg"
                            label="Close"
                            onClick={() => {
                                setOpen("false");
                            }}
                        />
                    </div>
                }
                ariaLabel="Students not added to roster dialog"
                hasCloseX={false}
                id="student-not-added"
                open={open}
                title={
                    <span className="warning-modal-title-color">
                        {collisionStudents?.length > 1 ? "Students" : "Student"} Cannot be Added
                    </span>
                }
            >
                {collisionStudents && collisionStudents.length > 0 && (
                    <div data-testid="collision-modal-content" style={{ overflow: "scroll", maxHeight: "500px" }}>
                        The following student(s) could not be added because they are currently rostered on another
                        homeroom Report Card. Please coordinate with the teacher to determine who will post the
                        student&apos;s progress report for the semester. If you need to add the student, the
                        student&apos;s former teacher will first need to go to their homeroom roster and remove the
                        student.
                        <ul style={{ marginTop: "8px" }}>
                            {collisionStudents?.map((student) => {
                                const {
                                    locationName,
                                    studentFirstName,
                                    studentLastName,
                                    studentNumber,
                                    teacherFirstName,
                                    teacherLastName
                                } = student;
                                return (
                                    <li key={studentNumber} style={{ marginBottom: "4px" }}>
                                        <b>
                                            {studentFirstName} {studentLastName}
                                        </b>{" "}
                                        is rostered by{" "}
                                        <b>
                                            {teacherFirstName} {teacherLastName}
                                        </b>{" "}
                                        at <b>{locationName}</b>.
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </DcsdDialog>
            {loader && <LoadingSvg />}
        </div>
    );
};

ManageRoster.propTypes = {
    dispatch: PropTypes.func.isRequired,
    path: PropTypes.string,
    state: PropTypes.object.isRequired,
    basePath: PropTypes.string,
    ownerUsername: PropTypes.string,
    schoolLevelTemplateDto: PropTypes.object
};

ManageRoster.defaultProps = {
    path: "",
    ownerUsername: null
};

export default ManageRoster;
